import React, { useState, useEffect, useRef } from 'react';
import { 
  SearchContainer,
  InputWrapper, 
  InputField, 
  SearchIcon, 
  ClearIcon, 
  SearchResults, 
  ExpandIconsContainer, 
  ExpandIconWrapper, 
  CancelText,
  LoadingIndicator,
} from './SearchComponent.styled';
import { Search } from '@mui/icons-material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import useDebounce from '../../../hooks/useDebounce';

const SearchComponent = ({ 
  searchTerm = '', 
  onSearchChange, 
  onNavigateNext, 
  onNavigatePrevious, 
  onClear, 
  onCancel,
  filteredCount = 0, 
  totalCount = 0,
  currentResultIndex = 0,
  isLoading = false,
  placeholder = "Search messages...",
  minLength = 0,
  debounceDelay = 1000
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [inputValue, setInputValue] = useState(searchTerm);
  const debouncedInputValue = useDebounce(inputValue, debounceDelay);
  const clearingRef = useRef(false);

  useEffect(() => {
    if (!clearingRef.current) {
      setInputValue(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedInputValue !== searchTerm && !clearingRef.current) {
      if (debouncedInputValue.length >= minLength) {
        onSearchChange(debouncedInputValue);
      }
    }
  }, [debouncedInputValue, onSearchChange, searchTerm, minLength]);

  const handleInputChange = (value) => {
    setInputValue(value);
  };

  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (!isSearchExpanded) {
      setInputValue('');
      onClear();
    } else {
      setInputValue('');
      if (onCancel) {
        onCancel();
      } else {
        onClear(); // Fallback a onClear si onCancel no está definido
      }
    }
  };

  const handleClearSearch = () => {
    if (clearingRef.current) return;
    
    clearingRef.current = true;
    setInputValue('');
    
    if (onClear) {
      onClear();
    }
    
    setTimeout(() => {
      clearingRef.current = false;
    }, 100);
  };

  const showMinLengthWarning = minLength > 0 && inputValue.length > 0 && inputValue.length < minLength;
  const displayIndex = filteredCount > 0 ? currentResultIndex + 1 : 0;
  const isSingleResult = filteredCount === 1;
  const isFirstResult = currentResultIndex === 0;
  const isLastResult = currentResultIndex === filteredCount - 1;
  const shouldDisablePrevButton = isSingleResult || isFirstResult;
  const shouldDisableNextButton = isSingleResult || isLastResult;

  const navigateNext = () => {
    if (!shouldDisableNextButton) {
      onNavigateNext();
    }
  };

  const navigatePrevious = () => {
    if (!shouldDisablePrevButton) {
      onNavigatePrevious();
    }
  };

  return (
    <SearchContainer data-testid="search-container">
      {isSearchExpanded ? (
        <>
          <InputWrapper data-testid="input-wrapper">
            <SearchIcon type="button" aria-label="Search" isExpanded={isSearchExpanded} onClick={toggleSearch}>
              <Search data-testid="search-icon" />
            </SearchIcon>

            <InputField
              data-testid="input-field"
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={placeholder}
              autoFocus
              hasError={showMinLengthWarning}
            />
            {inputValue && (
              <ClearIcon 
                type="button"
                aria-label="Clear search"
                data-testid="clear-icon"
                onClick={handleClearSearch}
                style={{ 
                  cursor: clearingRef.current ? 'default' : 'pointer',
                  opacity: clearingRef.current ? 0.5 : 1
                }}
              >
                <HighlightOffIcon style={{ width: 24, height: 24 }} />
              </ClearIcon>
            )}
          </InputWrapper>
          {isLoading && inputValue.length >= minLength ? (
            <LoadingIndicator data-testid="loading-indicator">
              <CircularProgress size={20} />
            </LoadingIndicator>
          ) : searchTerm && inputValue.length >= minLength && (
            <SearchResults data-testid="search-results">
              {filteredCount > 0 
                ? `${displayIndex} of ${filteredCount}`
                : `${filteredCount} of ${totalCount}`}
            </SearchResults>
          )}
          {searchTerm && filteredCount > 0 && inputValue.length >= minLength && (
            <ExpandIconsContainer data-testid="expand-icons-container">
              <ExpandIconWrapper 
                type="button"
                aria-label="Previous result"
                data-testid="previous-result-button"
                onClick={navigatePrevious}
                disabled={shouldDisablePrevButton}
                style={{ 
                  opacity: shouldDisablePrevButton ? 0.5 : 1,
                  cursor: shouldDisablePrevButton ? 'default' : 'pointer' 
                }}
              >
                <ExpandLessIcon />
              </ExpandIconWrapper>
              <ExpandIconWrapper 
                type="button"
                aria-label="Next result"
                data-testid="next-result-button"
                onClick={navigateNext}
                disabled={shouldDisableNextButton}
                style={{ 
                  opacity: shouldDisableNextButton ? 0.5 : 1,
                  cursor: shouldDisableNextButton ? 'default' : 'pointer' 
                }}
              >
                <ExpandMoreIcon />
              </ExpandIconWrapper>
            </ExpandIconsContainer>
          )}

          <CancelText onClick={toggleSearch} data-testid="cancel-button">Cancel</CancelText>
        </>
      ) : (
        <SearchIcon data-testid="search-icon" type="button" aria-label="Search" isExpanded={isSearchExpanded} onClick={toggleSearch}>
          <Search />
        </SearchIcon>
      )}
    </SearchContainer>
  );
};

export default SearchComponent; 